/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: CsrfToken.ts
 * Created by: Kevin
 * Create Date: 2026-08-11 11:40
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import axios from "axios";
import AppConfig from "@/commons/AppConfig";

/**
 * The client half of the server's double-submit CSRF check.
 *
 * The team-site server issues a `__x_csrf_token__<hex(host)>` cookie on every safe request and,
 * from then on, refuses any POST/PUT/PATCH/DELETE that does not echo that value back in the
 * X-CSRF-Token header — with a bare 403 that carries no CORS headers, so the browser reports it
 * as "No 'Access-Control-Allow-Origin' header is present" and the real cause never reaches the
 * console. Echoing the cookie is the whole protocol; there is nothing to fetch or negotiate.
 *
 * The cookie is deliberately not HttpOnly: reading it here is what it is for.
 */
export default class CsrfToken {

    /** The prefix flib_net's CSRF filter builds its cookie name from. */
    private static readonly CookiePrefix = "__x_csrf_token__";

    /** Methods the server validates. GET/HEAD/OPTIONS are skipped there, so skip them here. */
    private static readonly MutatingMethods = ["post", "put", "patch", "delete"];

    /** Guards against a second interceptor being stacked on the shared axios instance. */
    private static installed = false;

    /**
     * Installs the request interceptor that echoes the token on every mutating call.
     *
     * Call it from the application entry point, before anything issues a request. It deliberately
     * does not live in HttpExecutor.initialize(): nothing calls that method, so an interceptor
     * registered there is never installed — which is exactly how a header that "was added" can go
     * on being absent from every request.
     */
    public static install(): void {
        if (CsrfToken.installed) {
            return;
        }
        CsrfToken.installed = true;

        axios.interceptors.request.use((config) => {
            if (!CsrfToken.isMutating(config.method)) {
                return config;
            }
            const token = CsrfToken.resolve();
            if (token.length > 0) {
                config.headers.set("X-CSRF-Token", token);
            }
            return config;
        });
    }

    /** True when a request with this method must carry the token. */
    public static isMutating(method?: string | null): boolean {
        return CsrfToken.MutatingMethods.includes((method || "get").toLowerCase());
    }

    /**
     * The token to echo for the team-site API, or "" when the cookie was never stored.
     *
     * The name is keyed by the API host because the server names it after its own request Host —
     * the dashboard and the REST plane can be different ports of that host (local dev) or the same
     * origin (staging, production), and cookies ignore ports either way, so the page can always
     * read it. A proxy that rewrites Host would produce a name this cannot predict, hence the
     * fallback: any single cookie carrying the prefix is the one the server issued.
     */
    public static resolve(): string {
        const exact = CsrfToken.read(CsrfToken.cookieName(AppConfig.Instance.apiServiceTeam));
        if (exact.length > 0) {
            return exact;
        }
        return CsrfToken.readByPrefix();
    }

    /** `__x_csrf_token__` + the hex-encoded host (port included, exactly as flib_net encodes it). */
    private static cookieName(apiBaseURL: string): string {
        let host: string;
        try {
            host = new URL(apiBaseURL).host;
        } catch (e) {
            return "";
        }
        let hex = "";
        for (const byte of new TextEncoder().encode(host)) {
            hex += byte.toString(16).padStart(2, "0");
        }
        return CsrfToken.CookiePrefix + hex;
    }

    private static read(name: string): string {
        if (name.length === 0) {
            return "";
        }
        for (const part of document.cookie.split(";")) {
            const cookie = part.trim();
            if (cookie.startsWith(name + "=")) {
                return decodeURIComponent(cookie.substring(name.length + 1));
            }
        }
        return "";
    }

    private static readByPrefix(): string {
        for (const part of document.cookie.split(";")) {
            const cookie = part.trim();
            if (!cookie.startsWith(CsrfToken.CookiePrefix)) {
                continue;
            }
            const separator = cookie.indexOf("=");
            if (separator > 0) {
                return decodeURIComponent(cookie.substring(separator + 1));
            }
        }
        return "";
    }

}
